import type { NavigateFunction } from 'react-router-dom';

import { getServiceId, setServiceId } from '@/api/tokens.helper';
import { useActiveAccount, type ServiceType } from '@/store/active-account/active-account.store';
import { useAuth } from '@/store/auth/auth.store';
import { refreshAfterAccountSwitch } from '@/store/service-providers/refresh-active-account';

const SERVICE_ID_PARAM = 'service_id';

export type NotificationRedirectData = {
  link?: string;
  service_id?: string;
};

/**
 * Resolve a push `link` to a same-app path (pathname + search + hash), dropping the
 * `service_id` param so it doesn't linger in the address bar after we've applied it.
 * Returns null when the link doesn't resolve to an in-app route.
 */
export const toInAppPath = (link?: string | null): string | null => {
  if (!link) return null;
  try {
    const url = new URL(link, window.location.origin);
    url.searchParams.delete(SERVICE_ID_PARAM);
    const search = url.searchParams.toString();
    return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
  } catch {
    // Not a valid URL — accept it only if it already looks like a relative path.
    return link.startsWith('/') ? link : null;
  }
};

const extractServiceId = (link?: string | null): string | undefined => {
  if (!link) return undefined;
  try {
    return new URL(link, window.location.origin).searchParams.get(SERVICE_ID_PARAM) ?? undefined;
  } catch {
    return undefined;
  }
};

/**
 * Wait until the auth session is available (or time out). A push tap can launch the
 * app from cold, firing before `/me` resolves — and switching context needs the
 * session to verify service ownership first.
 */
const waitForSession = (timeoutMs = 8000): Promise<void> =>
  new Promise((resolve) => {
    if (useAuth.getState().session) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      unsubscribe();
      resolve();
    }, timeoutMs);
    const unsubscribe = useAuth.subscribe((state) => {
      if (state.session) {
        clearTimeout(timer);
        unsubscribe();
        resolve();
      }
    });
  });

/**
 * Switch the active account to `serviceId` when it belongs to the signed-in user and
 * isn't already active. Returns true when a switch actually happened.
 *
 * Security: only switches to a service present in the user's own service list, so a
 * crafted link/notification can't pull another user's data into view.
 */
const switchServiceContext = async (serviceId?: string): Promise<boolean> => {
  if (!serviceId) return false;

  const { session } = useAuth.getState();
  const ownedService = session?.services?.find((s) => s.service_id === serviceId);
  if (!ownedService || serviceId === getServiceId()) return false;

  const serviceType: ServiceType =
    ownedService.service_type === 'vessel' ? 'vessel' : 'service-provider';
  // Mirror Layout's deep-link handler: set state + persist the id (the axios
  // interceptor reads it as X-Service-Id) + refresh permissions before navigating,
  // so ProtectedRoute evaluates the target route with the new service's rights.
  useActiveAccount.setState({ serviceId, serviceType });
  setServiceId(serviceId);
  await useAuth.getState().updateRoleForVessel(serviceId);
  return true;
};

/**
 * Handle a push-notification redirect: switch to the originating service context
 * (email/SMS carry it in the link's `service_id` query; push carries it in
 * `data.service_id`) and navigate to the notification's target page.
 */
export const redirectFromNotification = async (
  data: NotificationRedirectData,
  navigate: NavigateFunction,
): Promise<void> => {
  const link = data?.link;
  const serviceId = data?.service_id || extractServiceId(link);
  const path = toInAppPath(link);

  if (serviceId) await waitForSession();
  const switched = await switchServiceContext(serviceId);

  if (path) navigate(path);

  // Reload data for the new context after navigating, so the redirect stays snappy.
  if (switched) void refreshAfterAccountSwitch();
};
